<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :hide-save-button="!modifiedData.isFromManager"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="name" class="text-sm font-medium">{{ $t('baseObject.name') }}</label>
        <BaseInput
          v-model="modifiedData.name"
          color="base"
          show-error-as-tooltip
          :error="$v.name.required.$invalid ? $v.name.required.$message : ''" />
      </div>
      <div :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <label for="name" class="text-sm font-medium">{{ $t('baseObject.code') }}</label>
        <BaseInput
          v-model="modifiedData.code"
          color="base"
          show-error-as-tooltip
          :error="$v.code.required.$invalid ? $v.code.required.$message : ''" />
      </div>
      <BaseToggle
        :class="{ 'pointer-events-none': !modifiedData.isFromManager }"
        :label="$t('baseObject.isActive')"
        name="isActive"
        v-model="modifiedData.isActive" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useToastStore } from '@/stores/toast';
import { addOrEditCompetitorType } from '@/services/api/traders';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { deepCopy } from '@/utils/data';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseModalEditCreate from '../common/BaseModalEditCreate.vue';

import type { CompetitorType } from '@/types/Traders';

const toastStore = useToastStore();
const { t: $t } = useI18n({ useScope: 'global' });
const emit = defineEmits(['closeModal', 'save']);
const isModified = ref(false);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    competitorType: CompetitorType;
  }>(),
  {}
);
const modifiedData = ref<CompetitorType>(deepCopy<CompetitorType>(props.competitorType));

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.name = newData.name || '';
    validationModel.value.code = newData.code || '';

    isModified.value = JSON.stringify(newData) !== JSON.stringify(props.competitorType);
  },
  { deep: true }
);

function onDismissChanges() {
  modifiedData.value = deepCopy<CompetitorType>(props.competitorType);
  emit('closeModal');
}

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  const res = await addOrEditCompetitorType(modifiedData.value);
  if (res) {
    emit('save', modifiedData.value);
    toastStore.success({ text: $t('common.toast.success') });
  } else {
    toastStore.error({ text: $t('common.toast.error') });
    emit('closeModal');
  }
}

const validationModel = ref({
  name: modifiedData.value.name || '',
  code: modifiedData.value.code || '',
});

const rules = computed(() => ({
  name: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  code: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });
</script>
